import { Strings } from 'Strings';
import getProductByDomain from './helpers/getProductByDomain';
import checkAnyProduct from './helpers/checkAnyProduct';

type AnalyticEnv = {
    prod: string;
    test: string;
};

type AnalyticId = {
    wave: AnalyticEnv;
    studio: AnalyticEnv;
};

export class Utils {
    static calcTime(time: number) {
        const { floor, ceil } = Math;

        const hrs = floor(time / 3600);
        const mins = floor((time % 3600) / 60);
        const secs = ceil(time % 60);

        // return '1:01' or '4:03:59' or '123:03:59'
        let ret = '';

        if (hrs > 0) {
            ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
        }

        ret += '' + mins + ':' + (secs < 10 ? '0' : '');
        ret += '' + secs;
        return ret;
    }

    static toHyphenCase(str: string): string {
        return str.replace(/([A-Z])/g, letter => {
            const letterLowerCase = letter.toLowerCase();
            return letterLowerCase.replace(
                letterLowerCase,
                '-' + letterLowerCase
            );
        });
    }
    
    static getDisplayPlanName(subscriptionName: string) {
        switch (true) {
          case subscriptionName.includes('Free'):
            return 'Free';
      
          case subscriptionName.includes('Creator'):
            return 'Creator';
      
          case subscriptionName.includes('Ultimate'):
            return 'Ultimate';
      
          case subscriptionName.includes('Agency'):
            return 'Agency';
      
          case subscriptionName.includes('Pro'):
            return 'Pro';
      
          case subscriptionName.includes('Corporate'):
            return 'Corporate';
      
          case subscriptionName.includes('Staff'):
            return 'Staff';
      
          case subscriptionName.includes('Influencer'):
            return 'Influencer';
      
          case subscriptionName.includes('Business'):
            return 'Business';
      
          case subscriptionName.includes('Enterprise'):
            return 'Enterprise';
      
          default:
            return subscriptionName as string;
        }
      };

    static getStateByProduct(suffix = '') {
        const product = Utils.getProduct();
        return product === 'WAVE' ? '' : `studio${suffix}`;
    }

    static scopedEvent(
        eventName: string | string[],
        scope: ng.IScope,
        func: EventListenerOrEventListenerObject,
        element: EventTarget = window,
        useCapture = false
    ): Function {
        const events: Array<string> = Array.isArray(eventName)
            ? eventName
            : [eventName];
        const destroy = events.map(name => {
            element.addEventListener(name, func, useCapture);
            scope.$on('$destroy', () =>
                element.removeEventListener(name, func)
            );
            return () => element.removeEventListener(name, func);
        });
        return () => destroy.forEach(f => f());
    }

    static getBrowserName() {
        const browsers = [
            'Opera',
            'Chrome',
            'Safari',
            'Firefox',
            'MSIE',
            'unknown'
        ];
        const index = browsers.findIndex(browser =>
            navigator.userAgent.includes(browser)
        );
        return browsers[index];
    }

    static oneTimeEvent(
        eventName: string | string[],
        scope: ng.IScope,
        func: EventListenerOrEventListenerObject,
        element: EventTarget = window
    ): Function {
        let destroy: Function | null = null;
        const oneTime = _.once((...args: any[]) => {
            (func as any)(...args);
            destroy && destroy();
        });
        destroy = Utils.scopedEvent(eventName, scope, oneTime, element);
        return destroy;
    }

    static isApiError(a: any, code?: number, msg?: RegExp): a is ApiError {
        return (
            !!a.code &&
            a.code >= 400 &&
            (!code || a.code === code) &&
            (!msg || msg.test(a.message))
        );
    }

    static isUserError(code: number) {
        return [440, 460].includes(code);
    }

    static handleUserSessionError(res: any) {
        if (Utils.isApiError(res) && Utils.isUserError(res.code)) {
            const url = Utils.isStudio() ? '/studio/user/error' : '/user/error';
            location.replace(url);
        }
    }

    static getProduct() {
        return checkAnyProduct() ? 'ANY' : getProductByDomain(location.host);
    }

    static getAuthProduct() {
        const product = Utils.getProduct();
        return product === 'ANY' ? 'STUDIO' : product;
    }

    static isStudio() {
        return Utils.getProduct() === 'STUDIO';
    }

    static isUser(a: any): a is anm.User {
        return a.username && a.displayName;
    }

    static getAnalyticId = ({ wave, studio }: AnalyticId) => {
        const { prod, test } = Utils.getProduct() === 'WAVE' ? wave : studio;
        return options.isProduction ? prod : test;
    };

    static getEnv = () => {
        return options.isProduction ? 'prod' : 'test';
    };

    static redirect(
        url: string,
        $location: ng.ILocationService,
        shouldReplace?: boolean
    ) {
        if (!url) {
            throw new Error('Empty url was passed to redirect');
        }

        if (/^http(s?):\/\//.test(url)) {
            location.href = url;
            return;
        }

        const [path, hash] = url.split('#');
        const [state, paramsStr] = path.split('?');
        const newLoc = $location.url(state).search(Strings.toParams(paramsStr));

        if (hash) {
            newLoc.hash(hash).replace();
        }

        if (!hash && shouldReplace) {
            newLoc.replace();
        }
    }

    static isSecureUser = (a: any): a is anm.SecureUser =>
        a.encryptedToken && a.userProfile;

    static sequence = (
        array: Array<string>,
        $ocLazyLoad: oc.ILazyLoad
    ): Promise<any> =>
        array.reduce(
            (acc, val) => acc.then(() => $ocLazyLoad.load(val)),
            Promise.resolve()
        );

    static toParams = (obj: any) =>
        Object.keys(obj)
            .map(k => `${k}=${encodeURIComponent(obj[k])}`)
            .join('&');

    static matchesSelector = (element: HTMLElement, selector: string) => {
        const ElementPrototype = Element.prototype;
        const fn =
            ElementPrototype.matches ||
            ElementPrototype.webkitMatchesSelector ||
            ElementPrototype.msMatchesSelector;
        return fn.call(element, selector);
    };

    static rAF = (function() {
        let lastTime = 0;
        const vendors = ['ms', 'moz', 'webkit', 'o'];
        for (
            let x = 0;
            x < vendors.length && !window.requestAnimationFrame;
            ++x
        ) {
            window.requestAnimationFrame =
                window[vendors[x] + 'RequestAnimationFrame'];
            window.cancelAnimationFrame =
                window[vendors[x] + 'CancelAnimationFrame'] ||
                window[vendors[x] + 'CancelRequestAnimationFrame'];
        }

        if (!window.requestAnimationFrame)
            window.requestAnimationFrame = (callback: FrameRequestCallback) => {
                const currTime = new Date().getTime();
                const timeToCall = Math.max(0, 16 - (currTime - lastTime));
                const id = window.setTimeout(
                    () => callback(currTime + timeToCall),
                    timeToCall
                );
                lastTime = currTime + timeToCall;
                return id;
            };

        if (!window.cancelAnimationFrame)
            window.cancelAnimationFrame = id => clearTimeout(id);
        return window.requestAnimationFrame.bind(window);
    })();

    // _.filterObj = (obj: any, pred: any) => _.object(Object.keys(obj).filter(k => pred(k, obj[k])).map(k => [k, obj[k]]));

    public static safe<T>(f: () => T) {
        try {
            f();
        } catch (e) {}
    }

    public static waitFor(check: () => void, cb: () => void) {
        if (check()) {
            cb();
        } else {
            setTimeout(() => Utils.waitFor(check, cb), 100);
        }
    }

    public static getMonthDaysCount() {
        const current = new Date();
        const d = new Date(current.getFullYear(), current.getMonth() + 1, 0);
        return d.getDate();
    }

    static curry(f: Function) {
        return function(...x: any[]) {
            return f.length <= 1 ? f(...x) : (f = f.bind(null, ...x));
        };
    }

    static createElement<K extends keyof HTMLElementTagNameMap>(
        name: K,
        classes?: string[],
        child?: HTMLElement
    ): HTMLElementTagNameMap[K] {
        const el = document.createElement<K>(name);
        if (classes) classes.forEach(className => el.classList.add(className));
        if (child) el.appendChild(child);

        return el;
    }

    static getRedirectToReactUrl(
        url: string,
        productName?: AnimatronProduct,
        params?: object
    ) {
        const urlParams = _.isEmpty(params) ? '' : `?${Utils.toParams(params)}`;
        return `${Utils.getWebsiteUrl(productName)}${url}${urlParams}`;
    }

    static redirectToReact(
        url: string,
        productName?: AnimatronProduct,
        params?: object
    ) {
        location.href = Utils.getRedirectToReactUrl(url, productName, params);
    }

    static getDomain(productName = Utils.getProduct()) {
        const localProductNameMap: { [key in AnimatronProduct]: string } = {
            ANY: 'localhost:3000/',
            WAVE: 'localhost:3005/',
            STUDIO: 'localhost:3000/'
        };

        const productNameMap: { [key in AnimatronProduct]: string } = {
            ANY: `animatron${options.isProduction ? '' : '-test'}.com/`,
            WAVE: `wave${options.isProduction ? '' : '-test'}.video/`,
            STUDIO: `animatron${
                options.isProduction ? '' : '-test'
            }.com/studio/`
        };

        return (options.isLocal ? localProductNameMap : productNameMap)[
            productName
        ];
    }

    static getWebsiteUrl(productName = Utils.getProduct()) {
        const protocol = options.isLocal ? 'http://' : 'https://';
        return `${protocol}${Utils.getDomain(productName)}`;
    }

    static checkReferrerUrl(url: string) {
        return new RegExp(url, 'gi').test(document.referrer);
    }

    static getEntry() {
        const entries = [
            { name: 'BUTTON', url: 'button' },
            { name: 'CALENDAR', url: 'calendar' },
            { name: 'CALENDAR', url: 'social-calendar' },
            { name: 'TEMPLATE', url: 'video-templates' }
        ];

        const entry = entries.find(({ url }) =>
            Utils.checkReferrerUrl(`/wave/${url}`)
        );
        return entry ? entry.name : 'LANDING';
    }
}

_.mixin(Utils);



// WEBPACK FOOTER //
// ./app/assets/javascripts/underscore-extensions.ts